const React              = require('react');
const PropTypes          = require('prop-types');
const documentDataCopy   = require('../../../../../copy/documentDataCopy');
const { getPageHeadByPath, getAllManagedMetaKeys } = require('../../../../../util/pageHead');
const { withTranslation } = require('react-i18next');

class DocumentData extends React.Component {
  constructor(props) {
    super(props);
    this.previousManagedMetaKeys = new Set();
  }

  componentDidMount() {
    this.applyDocumentData();
  }

  componentDidUpdate(prevProps) {
    const currentPathname = window.location.pathname;
    const propsChanged = prevProps.siteLocation !== this.props.siteLocation;

    if (propsChanged || this.lastAppliedPathname !== currentPathname) {
      this.applyDocumentData();
    }
  }

  applyDocumentData() {
    const currentPathname = window.location.pathname;
    const pageHead = getPageHeadByPath(currentPathname, {
      currentOrigin: window.location.origin,
    });

    if (pageHead && pageHead.hasPageHead) {
      this.setTitle(pageHead.title || 'Spacer by CBRE');
      this.applyManagedMetaTags(pageHead.metaTags);
      this.applySchemaScripts(pageHead.schemaItems || []);
      this.setCanonical(pageHead.canonical || '');
      this.lastAppliedPathname = currentPathname;
      return;
    }

    // For unknown pages, still apply static shared meta tags (robots, og:type, og:site_name)
    if (pageHead && pageHead.metaTags && pageHead.metaTags.length > 0) {
      this.applyManagedMetaTags(pageHead.metaTags);
    }

    const fallbackMetadata = documentDataCopy[this.props.siteLocation];
    if (fallbackMetadata) {
      const { titleToken, metaTagToken } = fallbackMetadata;
      this.setTitle(this.props.t(titleToken));
      if (!pageHead || !pageHead.metaTags || pageHead.metaTags.length === 0) {
        this.applyManagedMetaTags([
          {
            attributeName: 'name',
            attributeValue: 'description',
            content: this.props.t(metaTagToken),
          },
        ]);
      }
      this.setCanonical('');
      this.applySchemaScripts([]);
      this.lastAppliedPathname = currentPathname;
    }
  }

  setTitle(title) {
    document.title = title;
  }

  createMetaKey(attributeName, attributeValue) {
    return `${attributeName}::${attributeValue}`;
  }

  removeMetaTag(attributeName, attributeValue) {
    document.querySelectorAll(`meta[${attributeName}="${attributeValue}"]`).forEach((element) => {
      element.remove();
    });
  }

  insertNearTopOfHead(element) {
    const viewportMeta = document.head.querySelector('meta[name="viewport"]');

    if (!viewportMeta) {
      document.head.insertBefore(element, document.head.firstChild);
      return;
    }

    document.head.insertBefore(element, viewportMeta.nextSibling);
  }

  moveElementAfter(element, referenceElement) {
    if (!referenceElement || !referenceElement.parentNode) {
      this.insertNearTopOfHead(element);
      return;
    }

    const parent = referenceElement.parentNode;
    if (referenceElement.nextSibling) {
      parent.insertBefore(element, referenceElement.nextSibling);
      return;
    }

    parent.appendChild(element);
  }

  getLastMetaElement() {
    const metaElements = document.head.querySelectorAll('meta');
    if (!metaElements.length) return null;
    return metaElements[metaElements.length - 1];
  }

  applyManagedMetaTags(metaTags) {
    const globalManagedMetaKeys = getAllManagedMetaKeys();
    const nextManagedMetaKeys = new Set(
      metaTags
        .filter((tag) => tag && tag.content)
        .map((tag) => this.createMetaKey(tag.attributeName, tag.attributeValue))
    );

    const keysToRemove = new Set(
      [...globalManagedMetaKeys, ...this.previousManagedMetaKeys]
        .filter((key) => !nextManagedMetaKeys.has(key))
    );
    keysToRemove.forEach((key) => {
      const [attributeName, attributeValue] = key.split('::');
      this.removeMetaTag(attributeName, attributeValue);
    });

    metaTags.forEach((tag) => {
      if (!tag || !tag.content) return;

      let element = document.head.querySelector(`meta[${tag.attributeName}="${tag.attributeValue}"]`);

      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(tag.attributeName, tag.attributeValue);
        this.insertNearTopOfHead(element);
      }

      element.setAttribute('content', tag.content);
    });

    this.previousManagedMetaKeys = nextManagedMetaKeys;
  }

  setCanonical(href) {
    const canonicalElements = Array.from(document.head.querySelectorAll('link[rel="canonical"]'));
    let element = canonicalElements[0] || null;

    if (canonicalElements.length > 1) {
      canonicalElements.slice(1).forEach((duplicate) => duplicate.remove());
    }

    if (!href) {
      if (element) element.remove();
      return;
    }

    if (!element) {
      element = document.createElement('link');
      element.setAttribute('rel', 'canonical');
    }

    const lastMetaElement = this.getLastMetaElement();
    if (lastMetaElement) {
      this.moveElementAfter(element, lastMetaElement);
    } else {
      this.insertNearTopOfHead(element);
    }

    element.setAttribute('href', href);
  }

  applySchemaScripts(schemaItems) {
    document.head
      .querySelectorAll('script[type="application/ld+json"]')
      .forEach((scriptNode) => scriptNode.remove());

    let anchorElement = document.head.querySelector('link[rel="canonical"]') || this.getLastMetaElement();

    (schemaItems || []).forEach((schemaItem) => {
      if (!schemaItem || typeof schemaItem !== 'object') return;

      const scriptNode = document.createElement('script');
      scriptNode.setAttribute('type', 'application/ld+json');
      scriptNode.textContent = JSON.stringify(schemaItem)
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/&/g, '\\u0026');

      if (anchorElement) {
        this.moveElementAfter(scriptNode, anchorElement);
      } else {
        this.insertNearTopOfHead(scriptNode);
      }

      anchorElement = scriptNode;
    });
  }

  render() {
    return null;
  }
}

DocumentData.propTypes = {
  siteLocation: PropTypes.string.isRequired,
  t: PropTypes.func.isRequired
};

module.exports = withTranslation('common')(DocumentData);
